// Sensei AI panel — owner-only deep analysis result.
//
// Layout philosophy: "decision-first". The user sees the actionable picks
// at a glance (final bet + value bets); the chain-of-thought reasoning is
// hidden inside a collapsible "Dettagli analisi" accordion so the screen
// doesn't drown the user in text.

import { useState } from "react";
import {
  Sparkles, AlertTriangle, ShieldCheck, Loader2, Target,
  TrendingUp, X, AlertOctagon, ChevronDown, ChevronUp,
} from "lucide-react";
import { runSenseiAnalysis } from "@/lib/sensei";

const VERDICT_TONE = {
  CONFERMA:     { color: "var(--bc-accent)", label: "CONFERMA" },
  RIDIMENSIONA: { color: "#F6C643",          label: "RIDIMENSIONA" },
  INVERTE:      { color: "#EF4444",          label: "INVERTE" },
};
const ACTION_TONE = {
  BET:   { color: "var(--bc-accent)", label: "PUNTA" },
  HEDGE: { color: "#F6C643",          label: "COPERTURA" },
  SKIP:  { color: "var(--bc-sub, #71717A)",          label: "SALTA" },
};

export default function SenseiPanel({ fixtureId, matchPayload, onClose }) {
  const [state, setState] = useState("idle");
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const run = async () => {
    setState("running");
    setErr(null);
    try {
      const res = await runSenseiAnalysis(fixtureId, matchPayload || {});
      setData(res);
      setState("done");
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Errore sconosciuto");
      setState("error");
    }
  };

  return (
    <div
      data-testid="sensei-panel"
      style={{
        background: "color-mix(in srgb, var(--bc-accent) 4%, var(--bc-panel, #09090B))",
        border: "1px solid var(--bc-accent-medium)",
        boxShadow: "0 0 28px -10px var(--bc-glow)",
        padding: "1rem",
        marginTop: "1rem",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.8rem" }}>
        <Sparkles size={16} style={{ color: "var(--bc-accent)" }} />
        <span style={{
          fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 800,
          fontSize: "0.95rem", letterSpacing: "0.18em",
          textTransform: "uppercase", color: "var(--bc-accent)",
          textShadow: "0 0 10px var(--bc-glow)",
        }}>
          SENSEI AI
        </span>
        <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "0.58rem", letterSpacing: "0.18em", color: "rgba(255,255,255,0.4)" }}>
          OWNER
        </span>
        <div style={{ flex: 1 }} />
        {onClose && (
          <button onClick={onClose} data-testid="sensei-close"
            style={{ background: "transparent", border: "none", color: "rgba(255,255,255,0.5)", cursor: "pointer" }}>
            <X size={14} />
          </button>
        )}
      </div>

      {state === "idle" && (
        <div style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
          <p style={{ flex: 1, minWidth: 220, margin: 0, fontSize: "0.82rem", color: "rgba(255,255,255,0.6)", lineHeight: 1.4 }}>
            Devil's Advocate · Value Bet · Kelly Criterion
          </p>
          <button
            onClick={run}
            data-testid="sensei-run-btn"
            style={{
              padding: "0.65rem 1.2rem",
              background: "var(--bc-accent)",
              color: "#020202",
              border: "1px solid var(--bc-accent)",
              fontFamily: "'Barlow Condensed',sans-serif",
              fontWeight: 800,
              fontSize: "0.88rem",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              cursor: "pointer",
              boxShadow: "0 0 18px -4px var(--bc-glow)",
            }}
          >
            ⚔ Attiva il Sensei
          </button>
        </div>
      )}

      {state === "running" && (
        <div style={{ display: "flex", alignItems: "center", gap: "0.7rem", padding: "1.5rem 0", color: "var(--bc-accent)" }}>
          <Loader2 size={16} className="animate-spin" />
          <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "0.74rem", letterSpacing: "0.12em", textTransform: "uppercase" }}>
            Il Sensei sta meditando…
          </span>
        </div>
      )}

      {state === "error" && (
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", padding: "0.7rem", background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.3)" }}>
          <AlertOctagon size={14} style={{ color: "#EF4444" }} />
          <span style={{ fontSize: "0.82rem", color: "#FCA5A5", flex: 1 }}>{err}</span>
          <button onClick={run} data-testid="sensei-retry"
            style={{ padding: "0.3rem 0.8rem", background: "transparent", color: "#FCA5A5", border: "1px solid rgba(239,68,68,0.4)", fontFamily: "'Barlow Condensed',sans-serif", textTransform: "uppercase", letterSpacing: "0.12em", fontSize: "0.72rem", cursor: "pointer" }}>
            RIPROVA
          </button>
        </div>
      )}

      {state === "done" && data && <SenseiResult data={data} />}
    </div>
  );
}


function SenseiResult({ data }) {
  const final = data.final_recommendation || {};
  const action = ACTION_TONE[final.action] || ACTION_TONE.BET;
  const value_bets = data.value_bets || [];
  const combo = data.dc_goal_combo || null;
  const comboAlt = data.dc_goal_combo_alt || null;
  const teamMG = data.team_multigol_pick || null;
  const bayes = data.bayes || null;
  const [openDetails, setOpenDetails] = useState(false);

  return (
    <div data-testid="sensei-result" style={{ display: "flex", flexDirection: "column", gap: "0.7rem" }}>

      {/* ─── BAYES BADGE — visible whenever the smoothing actually altered probs */}
      {bayes && bayes.applied && (
        <div data-testid="sensei-bayes-badge" style={{
          display: "flex", alignItems: "center", gap: "0.4rem",
          padding: "0.35rem 0.65rem",
          background: "color-mix(in srgb, #A78BFA 8%, var(--bc-panel, #09090B))",
          border: "1px solid rgba(167,139,250,0.4)",
          fontFamily: "'JetBrains Mono',monospace",
          fontSize: "0.62rem",
          letterSpacing: "0.12em",
          color: "rgba(255,255,255,0.65)",
        }}>
          <span style={{ color: "#A78BFA", fontWeight: 700, letterSpacing: "0.18em" }}>BAYES</span>
          <span style={{ opacity: 0.4 }}>·</span>
          <span>α {Math.round((bayes.alpha || 0) * 100)}% modello / {100 - Math.round((bayes.alpha || 0) * 100)}% mercato</span>
          {Object.keys(bayes.deltas || {}).length > 0 && (
            <>
              <span style={{ opacity: 0.4 }}>·</span>
              <span style={{ color: "rgba(255,255,255,0.5)" }}>
                smussati {Object.keys(bayes.deltas).length} mercati
              </span>
            </>
          )}
        </div>
      )}

      {/* ─── HERO: Final pick — large, immediately readable ──────────── */}
      <div style={{
        background: `color-mix(in srgb, ${action.color} 9%, var(--bc-panel, #09090B))`,
        border: `1.5px solid ${action.color}`,
        boxShadow: `0 0 22px -6px ${action.color}, inset 0 0 22px -10px ${action.color}`,
        padding: "1rem 1.1rem",
        position: "relative",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem" }}>
          <Target size={13} style={{ color: action.color }} />
          <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: "0.66rem", letterSpacing: "0.32em", textTransform: "uppercase", color: action.color, fontWeight: 700 }}>
            Pronostico Finale
          </span>
          <div style={{ flex: 1 }} />
          <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "0.66rem", letterSpacing: "0.14em", color: "rgba(255,255,255,0.5)" }}>
            AI {data.ai_score}/100
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "baseline", gap: "0.8rem", flexWrap: "wrap", marginBottom: 6 }}>
          <span data-testid="sensei-final-action" style={{
            fontFamily: "'Barlow Condensed',sans-serif",
            fontSize: "1.8rem",
            fontWeight: 900,
            letterSpacing: "0.14em",
            color: action.color,
            textTransform: "uppercase",
            textShadow: `0 0 14px ${action.color}`,
            lineHeight: 1,
          }}>
            {action.label}
          </span>
          <span data-testid="sensei-final-bet" style={{
            fontFamily: "'Barlow Condensed',sans-serif",
            fontSize: "1.35rem",
            fontWeight: 700,
            color: "var(--bc-ink, #FFFFFF)",
            lineHeight: 1.05,
          }}>
            {final.bet || "—"}
          </span>
        </div>

        <div style={{ display: "flex", gap: "0.7rem", flexWrap: "wrap", fontFamily: "'JetBrains Mono',monospace", fontSize: "0.7rem", letterSpacing: "0.1em" }}>
          <Pill label="Confidence" value={final.confidence || "MEDIA"} color={action.color} />
          <Pill label="Stake" value={`${final.stake_pct_bankroll || 0}%`} color="#F6C643" />
          {data.key_factor && (
            <span style={{ color: "rgba(255,255,255,0.45)", fontStyle: "italic", fontSize: "0.72rem" }}>
              · {data.key_factor}
            </span>
          )}
        </div>
      </div>

      {/* ─── DC + GOAL COMBO — mandatory safety-net combo ──────────────── */}
      {combo && combo.dc && combo.goal && (
        <div data-testid="sensei-dc-goal-combo" style={{
          background: "color-mix(in srgb, #06B6D4 7%, var(--bc-panel, #09090B))",
          border: "1px solid #06B6D4",
          boxShadow: "0 0 18px -6px #06B6D4, inset 0 0 14px -8px #06B6D4",
          padding: "0.85rem 1rem",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginBottom: "0.4rem" }}>
            <ShieldCheck size={12} style={{ color: "#06B6D4" }} />
            <span style={{
              fontFamily: "'Barlow Condensed',sans-serif", fontSize: "0.62rem",
              letterSpacing: "0.28em", textTransform: "uppercase",
              color: "#06B6D4", fontWeight: 700,
            }}>
              Combo DC + Mercato Gol
            </span>
            <div style={{ flex: 1 }} />
            <span style={{
              fontFamily: "'JetBrains Mono',monospace", fontSize: "0.6rem",
              letterSpacing: "0.14em", color: "rgba(255,255,255,0.5)",
              padding: "0.1rem 0.4rem", border: "1px solid rgba(255,255,255,0.15)",
            }}>
              {combo.confidence || "MEDIA"}
            </span>
          </div>
          <div style={{
            display: "flex", alignItems: "baseline", gap: "0.6rem",
            flexWrap: "wrap", marginBottom: 6,
          }}>
            <span data-testid="sensei-combo-bet" style={{
              fontFamily: "'Barlow Condensed',sans-serif",
              fontSize: "1.1rem", fontWeight: 800, color: "var(--bc-ink, #FFFFFF)",
              lineHeight: 1.15,
            }}>
              {combo.dc} <span style={{ color: "#06B6D4", margin: "0 0.3em" }}>+</span> {combo.goal}
            </span>
          </div>
          <div style={{
            display: "flex", gap: "0.6rem", flexWrap: "wrap", alignItems: "center",
            fontFamily: "'JetBrains Mono',monospace", fontSize: "0.7rem",
          }}>
            <span style={{ color: "#06B6D4", fontWeight: 700 }}>
              {Number(combo.combined_prob || 0).toFixed(1)}%
            </span>
            {combo.fair_odds && (
              <>
                <span style={{ color: "rgba(255,255,255,0.3)" }}>·</span>
                <span style={{ color: "rgba(255,255,255,0.6)" }}>
                  fair @ {Number(combo.fair_odds).toFixed(2)}
                </span>
              </>
            )}
          </div>
          {combo.rationale && (
            <p style={{
              margin: "0.45rem 0 0", fontSize: "0.74rem",
              color: "rgba(255,255,255,0.65)", lineHeight: 1.4,
            }}>
              {combo.rationale}
            </p>
          )}
        </div>
      )}

      {/* ─── DC + GOAL COMBO · ALTERNATIVA — backup di categoria diversa ─ */}
      {comboAlt && comboAlt.dc && comboAlt.goal && (
        <div data-testid="sensei-dc-goal-combo-alt" style={{
          background: "color-mix(in srgb, #A78BFA 5%, var(--bc-panel, #09090B))",
          border: "1px dashed rgba(167,139,250,0.55)",
          padding: "0.7rem 0.9rem",
          opacity: 0.92,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginBottom: "0.35rem" }}>
            <ShieldCheck size={11} style={{ color: "#A78BFA", opacity: 0.85 }} />
            <span style={{
              fontFamily: "'Barlow Condensed',sans-serif", fontSize: "0.58rem",
              letterSpacing: "0.28em", textTransform: "uppercase",
              color: "#A78BFA", fontWeight: 700,
            }}>
              Combo Alternativa · Backup
            </span>
            <div style={{ flex: 1 }} />
            <span style={{
              fontFamily: "'JetBrains Mono',monospace", fontSize: "0.56rem",
              letterSpacing: "0.14em", color: "rgba(255,255,255,0.45)",
              padding: "0.08rem 0.36rem", border: "1px solid rgba(167,139,250,0.3)",
            }}>
              {comboAlt.confidence || "MEDIA"}
            </span>
          </div>
          <div style={{
            display: "flex", alignItems: "baseline", gap: "0.5rem",
            flexWrap: "wrap", marginBottom: 4,
          }}>
            <span data-testid="sensei-combo-alt-bet" style={{
              fontFamily: "'Barlow Condensed',sans-serif",
              fontSize: "0.98rem", fontWeight: 700, color: "rgba(255,255,255,0.92)",
              lineHeight: 1.15,
            }}>
              {comboAlt.dc} <span style={{ color: "#A78BFA", margin: "0 0.25em" }}>+</span> {comboAlt.goal}
            </span>
          </div>
          <div style={{
            display: "flex", gap: "0.55rem", flexWrap: "wrap", alignItems: "center",
            fontFamily: "'JetBrains Mono',monospace", fontSize: "0.66rem",
          }}>
            <span style={{ color: "#A78BFA", fontWeight: 700 }}>
              {Number(comboAlt.combined_prob || 0).toFixed(1)}%
            </span>
            {comboAlt.fair_odds && (
              <>
                <span style={{ color: "rgba(255,255,255,0.3)" }}>·</span>
                <span style={{ color: "rgba(255,255,255,0.55)" }}>
                  fair @ {Number(comboAlt.fair_odds).toFixed(2)}
                </span>
              </>
            )}
          </div>
          {comboAlt.rationale && (
            <p style={{
              margin: "0.4rem 0 0", fontSize: "0.72rem",
              color: "rgba(255,255,255,0.6)", lineHeight: 1.4,
            }}>
              {comboAlt.rationale}
            </p>
          )}
        </div>
      )}

      {/* ─── TEAM MULTIGOL PICK — KAGE-style double-conviction ───────── */}
      {teamMG && teamMG.active && teamMG.home_band && teamMG.away_band && (
        <div data-testid="sensei-team-multigol" style={{
          background: "color-mix(in srgb, #22FFA3 7%, var(--bc-panel, #09090B))",
          border: "1px solid rgba(34,255,163,0.55)",
          boxShadow: "0 0 18px -6px rgba(34,255,163,0.55), inset 0 0 14px -8px rgba(34,255,163,0.55)",
          padding: "0.85rem 1rem",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginBottom: "0.4rem" }}>
            <ShieldCheck size={12} style={{ color: "#22FFA3" }} />
            <span style={{
              fontFamily: "'Barlow Condensed',sans-serif", fontSize: "0.62rem",
              letterSpacing: "0.28em", textTransform: "uppercase",
              color: "#22FFA3", fontWeight: 700,
            }}>
              Multigol per Squadra · Double-conviction
            </span>
            <div style={{ flex: 1 }} />
            <span style={{
              fontFamily: "'JetBrains Mono',monospace", fontSize: "0.6rem",
              letterSpacing: "0.14em", color: "rgba(255,255,255,0.55)",
              padding: "0.1rem 0.4rem", border: "1px solid rgba(34,255,163,0.4)",
            }}>
              {teamMG.confidence || "MEDIA"}
            </span>
          </div>
          <div style={{
            display: "flex", alignItems: "baseline", gap: "0.5rem",
            flexWrap: "wrap", marginBottom: 6,
          }}>
            <span data-testid="sensei-team-mg-bet" style={{
              fontFamily: "'Barlow Condensed',sans-serif",
              fontSize: "1.1rem", fontWeight: 700, color: "var(--bc-ink, #FFFFFF)",
              lineHeight: 1.15,
            }}>
              Multigol Casa <span style={{ color: "#22FFA3" }}>{teamMG.home_band}</span>
              <span style={{ color: "#22FFA3", margin: "0 0.3em" }}>+</span>
              Multigol Trasferta <span style={{ color: "#22FFA3" }}>{teamMG.away_band}</span>
            </span>
          </div>
          <div style={{
            display: "flex", gap: "0.55rem", flexWrap: "wrap", alignItems: "center",
            fontFamily: "'JetBrains Mono',monospace", fontSize: "0.68rem",
          }}>
            <span style={{ color: "#22FFA3", fontWeight: 700 }}>
              {Number(teamMG.combined_prob || 0).toFixed(1)}%
            </span>
            <span style={{ color: "rgba(255,255,255,0.3)" }}>·</span>
            <span style={{ color: "rgba(255,255,255,0.55)" }}>
              prob. quasi-indipendenza
            </span>
          </div>
          {teamMG.rationale && (
            <p style={{
              margin: "0.4rem 0 0", fontSize: "0.72rem",
              color: "rgba(255,255,255,0.65)", lineHeight: 1.4,
            }}>
              {teamMG.rationale}
            </p>
          )}
        </div>
      )}

      {/* ─── VALUE BETS — top picks, big readable cards ──────────────── */}
      {value_bets.length > 0 && (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginBottom: "0.4rem" }}>
            <TrendingUp size={12} style={{ color: "var(--bc-accent)" }} />
            <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: "0.62rem", letterSpacing: "0.28em", textTransform: "uppercase", color: "var(--bc-accent)", fontWeight: 700 }}>
              Value Bets {value_bets.length > 1 ? `· ${value_bets.length}` : ""}
            </span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "0.45rem" }}>
            {value_bets.map((vb, i) => <ValueBetCard key={i} vb={vb} />)}
          </div>
        </div>
      )}

      {/* ─── DETAILS ACCORDION — optional, starts collapsed ──────────── */}
      <button
        onClick={() => setOpenDetails(!openDetails)}
        data-testid="sensei-toggle-details"
        style={{
          marginTop: "0.2rem",
          padding: "0.55rem 0.7rem",
          background: "transparent",
          border: "1px solid var(--bc-border, rgba(255,255,255,0.07))",
          color: "rgba(255,255,255,0.65)",
          fontFamily: "'Barlow Condensed',sans-serif",
          fontSize: "0.72rem",
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          transition: "all 0.15s ease",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--bc-accent-medium)"; e.currentTarget.style.color = "#FFFFFF"; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--bc-border, rgba(255,255,255,0.07))"; e.currentTarget.style.color = "rgba(255,255,255,0.65)"; }}
      >
        <span>{openDetails ? "Nascondi analisi" : "Mostra analisi completa"}</span>
        {openDetails ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
      </button>

      {openDetails && <DetailedAnalysis data={data} />}
    </div>
  );
}


function Pill({ label, value, color }) {
  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      gap: "0.35rem",
      padding: "0.18rem 0.55rem",
      background: "rgba(0,0,0,0.35)",
      border: `1px solid ${color}`,
      fontSize: "0.66rem",
    }}>
      <span style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.6rem", letterSpacing: "0.16em" }}>
        {label}
      </span>
      <span style={{ color, fontWeight: 700, letterSpacing: "0.06em" }}>
        {value}
      </span>
    </span>
  );
}


function ValueBetCard({ vb }) {
  const tier =
    vb.edge_pct >= 12 ? { label: "PRIME", color: "var(--bc-accent)" } :
    vb.edge_pct >= 7  ? { label: "GOOD",  color: "#06B6D4" } :
                        { label: "EDGE",  color: "#F6C643" };
  return (
    <div style={{
      background: `color-mix(in srgb, ${tier.color} 6%, var(--bc-panel, #0A0A0A))`,
      border: `1px solid ${tier.color}`,
      padding: "0.65rem 0.75rem",
      display: "flex",
      flexDirection: "column",
      gap: "0.3rem",
      minHeight: 90,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "0.55rem", letterSpacing: "0.18em", padding: "0.1rem 0.35rem", background: tier.color, color: "#020202", fontWeight: 800 }}>
          {tier.label}
        </span>
        <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "0.78rem", color: tier.color, fontWeight: 800, letterSpacing: "0.04em" }}>
          +{vb.edge_pct}%
        </span>
        <div style={{ flex: 1 }} />
        <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "0.66rem", color: "#F6C643", fontWeight: 700 }}>
          {vb.kelly_stake_pct}% K
        </span>
      </div>
      <div style={{ fontSize: "0.92rem", fontWeight: 700, color: "var(--bc-ink, #FFFFFF)", lineHeight: 1.2 }}>
        {vb.bet}
      </div>
      <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "0.65rem", color: "rgba(255,255,255,0.55)", letterSpacing: "0.04em" }}>
        {vb.model_prob}% × {vb.market_odds.toFixed(2)}
      </div>
    </div>
  );
}


function DetailedAnalysis({ data }) {
  const verdict = VERDICT_TONE[data.verdict] || VERDICT_TONE.RIDIMENSIONA;
  const final = data.final_recommendation || {};

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.55rem", padding: "0.6rem 0.2rem 0.2rem" }}>
      {/* Verdict + rationale */}
      <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
        <span style={{
          padding: "0.18rem 0.55rem",
          background: `color-mix(in srgb, ${verdict.color} 14%, transparent)`,
          color: verdict.color,
          fontFamily: "'Barlow Condensed',sans-serif",
          fontSize: "0.66rem",
          fontWeight: 800,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          border: `1px solid ${verdict.color}`,
        }}>
          {verdict.label}
        </span>
        {final.rationale && (
          <span style={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.7)", lineHeight: 1.45, flex: 1, minWidth: 200 }}>
            {final.rationale}
          </span>
        )}
      </div>

      {/* Step 1 — short list */}
      {(data.step1_devil_advocate || []).length > 0 && (
        <Section label="Devil's Advocate" color="#EF4444">
          <ul style={{ margin: 0, paddingLeft: "1rem", fontSize: "0.78rem", color: "rgba(255,255,255,0.72)", lineHeight: 1.5 }}>
            {data.step1_devil_advocate.map((d, i) => <li key={i}>{d}</li>)}
          </ul>
        </Section>
      )}

      {/* Step 2 — single line */}
      {data.step2_evidence_check && (
        <Section label="Evidenza" color="#06B6D4">
          <p style={{ margin: 0, fontSize: "0.78rem", color: "rgba(255,255,255,0.72)", lineHeight: 1.5 }}>
            {data.step2_evidence_check}
          </p>
        </Section>
      )}

      {/* Step 3 — synthesis */}
      {data.step3_synthesis && (
        <Section label="Sintesi" color="var(--bc-accent)">
          <p style={{ margin: 0, fontSize: "0.78rem", color: "rgba(255,255,255,0.78)", lineHeight: 1.5 }}>
            {data.step3_synthesis}
          </p>
        </Section>
      )}

      {/* Skip reasons */}
      {(data.skip_reasons || []).length > 0 && (
        <Section label="Da evitare" color="#EF4444" Icon={AlertTriangle}>
          <ul style={{ margin: 0, paddingLeft: "1rem", fontSize: "0.74rem", color: "rgba(255,255,255,0.65)", lineHeight: 1.5 }}>
            {data.skip_reasons.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </Section>
      )}

      {/* Calibration warning */}
      {(data.calibration?.warnings || []).length > 0 && (
        <Section label="Auto-calibrazione" color="#F6C643" Icon={ShieldCheck}>
          {data.calibration.warnings.map((w, i) => (
            <p key={i} style={{ margin: 0, fontSize: "0.74rem", color: "rgba(255,255,255,0.65)", lineHeight: 1.5 }}>
              {w}
            </p>
          ))}
        </Section>
      )}
    </div>
  );
}


function Section({ label, color, Icon, children }) {
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: "0.35rem", marginBottom: 3 }}>
        {Icon && <Icon size={11} style={{ color }} />}
        <span style={{
          fontFamily: "'JetBrains Mono',monospace",
          fontSize: "0.58rem",
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color,
        }}>
          {label}
        </span>
      </div>
      {children}
    </div>
  );
}
